// Practik — shared income & tax calculation.
// Used by BOTH the Node backend (source of truth, /api/calculate) and the
// React frontend (instant slider feedback). Pure functions, no dependencies.
//
// Tax figures are UK self-employed (sole trader) estimates for the 2025/26 tax
// year, England/Wales/NI rates. They are a guide, not advice. Scotland has
// different income-tax bands.

export const DEFAULTS = {
  weeksPerYear: 46, // ~6 weeks off for holidays / sickness
  daysPerWeek: 5, // used to annualise "per day" costs
};

// 2025/26 thresholds & rates
const PERSONAL_ALLOWANCE = 12570;
const BASIC_LIMIT = 50270; // top of 20% band
const HIGHER_LIMIT = 125140; // top of 40% band

// The same thresholds, exported for anything that needs to *talk about* the
// bands rather than apply them — the generated /earnings/ pages do.
export const THRESHOLDS = {
  personalAllowance: PERSONAL_ALLOWANCE,
  basicLimit: BASIC_LIMIT,
  higherLimit: HIGHER_LIMIT,
};

function round(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

// Personal allowance is tapered away £1 for every £2 earned over £100,000.
function personalAllowance(profit) {
  if (profit <= 100000) return PERSONAL_ALLOWANCE;
  return Math.max(0, PERSONAL_ALLOWANCE - (profit - 100000) / 2);
}

// UK income tax on annual self-employed profit (2025/26 estimate).
export function incomeTax(profit) {
  if (profit <= 0) return 0;
  const pa = personalAllowance(profit);
  let tax = 0;
  let p = profit;
  if (p > HIGHER_LIMIT) {
    tax += (p - HIGHER_LIMIT) * 0.45;
    p = HIGHER_LIMIT;
  }
  if (p > BASIC_LIMIT) {
    tax += (p - BASIC_LIMIT) * 0.4;
    p = BASIC_LIMIT;
  }
  if (p > pa) {
    tax += (p - pa) * 0.2;
  }
  return tax;
}

// Class 4 National Insurance on annual profit (2025/26 estimate).
// Class 2 NICs are no longer payable for most self-employed people.
export function nationalInsurance(profit) {
  if (profit <= PERSONAL_ALLOWANCE) return 0;
  let ni = 0;
  let p = profit;
  if (p > BASIC_LIMIT) {
    ni += (p - BASIC_LIMIT) * 0.02;
    p = BASIC_LIMIT;
  }
  if (p > PERSONAL_ALLOWANCE) {
    ni += (p - PERSONAL_ALLOWANCE) * 0.06;
  }
  return ni;
}

// Main entry point. Takes raw inputs, returns a full annual breakdown plus
// handy weekly / monthly figures.
export function computeIncome(input = {}) {
  const sessionsPerWeek = Math.max(0, Number(input.sessionsPerWeek) || 0);
  const feePerSession = Math.max(0, Number(input.feePerSession) || 0);
  const costPerSession = Math.max(0, Number(input.costPerSession) || 0);
  const costPerDay = Math.max(0, Number(input.costPerDay) || 0);
  const costPerMonth = Math.max(0, Number(input.costPerMonth) || 0);
  const weeksPerYear = Math.max(1, Number(input.weeksPerYear) || DEFAULTS.weeksPerYear);
  const daysPerWeek = Math.max(1, Number(input.daysPerWeek) || DEFAULTS.daysPerWeek);

  const annualSessions = sessionsPerWeek * weeksPerYear;
  const grossAnnual = annualSessions * feePerSession;

  const costFromSessions = costPerSession * annualSessions;
  const costFromDays = costPerDay * daysPerWeek * weeksPerYear;
  const costFromMonths = costPerMonth * 12;
  const costsAnnual = costFromSessions + costFromDays + costFromMonths;

  const profitAnnual = grossAnnual - costsAnnual; // taxable profit
  const tax = incomeTax(profitAnnual);
  const ni = nationalInsurance(profitAnnual);
  const takeHomeAnnual = profitAnnual - tax - ni;

  // What to put aside for the tax bill so it never stings.
  const setAsideAnnual = tax + ni;
  const effectiveTaxRate = profitAnnual > 0 ? setAsideAnnual / profitAnnual : 0;

  return {
    inputs: {
      sessionsPerWeek,
      feePerSession,
      costPerSession,
      costPerDay,
      costPerMonth,
      weeksPerYear,
      daysPerWeek,
    },
    annualSessions: round(annualSessions),
    gross: round(grossAnnual),
    costs: round(costsAnnual),
    costBreakdown: {
      perSession: round(costFromSessions),
      perDay: round(costFromDays),
      perMonth: round(costFromMonths),
    },
    profit: round(profitAnnual),
    tax: round(tax),
    nationalInsurance: round(ni),
    takeHome: round(takeHomeAnnual),
    // convenient period views of take-home pay
    takeHomeMonthly: round(takeHomeAnnual / 12),
    takeHomeWeekly: round(takeHomeAnnual / weeksPerYear),
    grossMonthly: round(grossAnnual / 12),
    costsMonthly: round(costsAnnual / 12),
    // tax set-aside guidance
    setAsideAnnual: round(setAsideAnnual),
    setAsideMonthly: round(setAsideAnnual / 12),
    setAsidePerSession: round(annualSessions ? setAsideAnnual / annualSessions : 0),
    effectiveTaxRate: Math.round(effectiveTaxRate * 100), // whole %
  };
}

// --- Goal seek -------------------------------------------------------------
// "To take home £X, what do I need?" Take-home rises monotonically with both
// fee and sessions, so a binary search converges fast and exactly.

function solveFor(targetAnnualTakeHome, input, key, hi) {
  if (!(targetAnnualTakeHome > 0)) return 0;
  let lo = 0;
  let h = hi;
  // If even the cap can't reach the target, signal "more than cap".
  if (computeIncome({ ...input, [key]: h }).takeHome < targetAnnualTakeHome) {
    return { value: h, capped: true };
  }
  for (let i = 0; i < 50; i++) {
    const mid = (lo + h) / 2;
    if (computeIncome({ ...input, [key]: mid }).takeHome < targetAnnualTakeHome) lo = mid;
    else h = mid;
  }
  return { value: h, capped: false };
}

// Fee per session needed (sessions held at current level).
export function requiredFee(targetAnnualTakeHome, input) {
  const r = solveFor(targetAnnualTakeHome, input, "feePerSession", 1000);
  return { ...r, value: Math.ceil(r.value) };
}

// Sessions per week needed (fee held at current level).
export function requiredSessions(targetAnnualTakeHome, input) {
  const r = solveFor(targetAnnualTakeHome, input, "sessionsPerWeek", 80);
  return { ...r, value: Math.ceil(r.value) };
}

// --- Making Tax Digital (MTD for Income Tax) -------------------------------
// MTD is phased in by *qualifying income* — your GROSS self-employed (and
// property) turnover, NOT your profit. So we test against gross, not take-home.
// Dates/thresholds follow HMRC's announced timetable; these are a rough guide,
// not advice — confirm the current rules before relying on them.
export const MTD_PHASES = [
  { from: 50000, taxYear: "April 2026" },
  { from: 30000, taxYear: "April 2027" },
  { from: 20000, taxYear: "April 2028" },
];

// Given annual gross income, returns whether (and from when) the person is
// likely brought into MTD for Income Tax.
export function mtdScope(grossAnnual) {
  const gross = Math.max(0, Number(grossAnnual) || 0);
  const phase = MTD_PHASES.find((p) => gross >= p.from);
  return phase ? { inScope: true, from: phase.taxYear } : { inScope: false, from: null };
}
